/**
 * AI Settings - 常量配置
 */

import type { LanguageOption } from './types';
import { getDefaultLocale } from '@/i18n';

/**
 * 支持的语言列表
 */
export const SUPPORTED_LANGUAGES: readonly LanguageOption[] = [
    {
        code: 'en',
        label: 'English',
        nativeLabel: 'English',
    },
    {
        code: 'zh-CN',
        label: 'Simplified Chinese',
        nativeLabel: '简体中文',
    },
    {
        code: 'zh-TW',
        label: 'Traditional Chinese',
        nativeLabel: '繁體中文',
    },
    {
        code: 'ja',
        label: 'Japanese',
        nativeLabel: '日本語',
    },
    {
        code: 'ko',
        label: 'Korean',
        nativeLabel: '한국어',
    },
    {
        code: 'de',
        label: 'German',
        nativeLabel: 'Deutsch',
    },
    {
        code: 'fr',
        label: 'French',
        nativeLabel: 'Français',
    },
    {
        code: 'es',
        label: 'Spanish',
        nativeLabel: 'Español',
    },
    {
        code: 'pt',
        label: 'Portuguese',
        nativeLabel: 'Português',
    },
    {
        code: 'ru',
        label: 'Russian',
        nativeLabel: 'Русский',
    },
] as const;

/**
 * Region到语言的映射
 */
const REGION_LANGUAGE_MAP: Record<string, string> = {
    CN: 'en',
    TW: 'zh-TW',
    JP: 'ja',
    WEST: 'en',
    US: 'en',
    EU: 'en',
    UK: 'en',
    AU: 'en',
};

export function getDefaultLanguageByRegion(): string {
    const defaultLocale = getDefaultLocale();
    return REGION_LANGUAGE_MAP[defaultLocale.toUpperCase()] || 'en';
}

/**
 * 根据语言code获取完整的语言配置
 */
export function getLanguageByCode(code: string): LanguageOption | undefined {
    return SUPPORTED_LANGUAGES.find((lang) => lang.code === code);
}

/**
 * 根据语言code获取发送给AI的label
 */
export function getAILabelByCode(code: string): string {
    const lang = getLanguageByCode(code);
    return lang?.label || 'English';
}

/**
 * 根据语言code获取请求头 Accept-Language
 */
export function getAcceptLanguageByCode(code: string): string {
    const langMap: Record<string, string> = {
        'en': 'en',
        'zh-CN': 'zh-hans',
        'zh-TW': 'zh-hant',
        'ja': 'ja',
        'ko': 'ko',
        'de': 'de',
        'fr': 'fr',
        'es': 'es',
        'pt': 'pt',
        'ru': 'ru',
    };

    return langMap[code] || 'en';
}

/**
 * LocalStorage key
 */
export const STORAGE_KEY = 'ai-output-language';
